import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import { fontService } from '../../../services/fontService';
import { getAllPdfBackgrounds } from '../../../services/galleryService';

const PersonalizationOptions = ({ 
  personalization, 
  onPersonalizationChange,
  className = "" 
}) => {
  const [showQuotePreview, setShowQuotePreview] = useState(false);
  const [contentFonts, setContentFonts] = useState([]);
  const [titleFonts, setTitleFonts] = useState([]);
  const [isLoadingFonts, setIsLoadingFonts] = useState(true);
  const [backgroundImages, setBackgroundImages] = useState([]);
  const [isLoadingBackgrounds, setIsLoadingBackgrounds] = useState(true);

  // Load fonts from database on component mount
  useEffect(() => {
    loadFontsFromDatabase();
    loadBackgroundImages();
  }, []);

  // NEW: Load background images from gallery storage
  const loadBackgroundImages = async () => {
    try {
      console.log('🖼️ Loading PDF background images from gallery...');
      setIsLoadingBackgrounds(true);
      const backgrounds = await getAllPdfBackgrounds();
      console.log('✅ Background images loaded:', backgrounds);
      console.log('📊 Number of backgrounds found:', backgrounds?.length || 0);
      
      if (backgrounds && backgrounds?.length > 0) {
        console.log('🎨 First background sample:', backgrounds?.[0]);
        setBackgroundImages(backgrounds);
      } else {
        console.warn('⚠️ No PDF background images found in gallery bucket');
        setBackgroundImages([]);
      }
    } catch (error) {
      console.error('❌ Error loading background images:', error);
      console.error('Error details:', error?.message, error?.stack);
      setBackgroundImages([]);
    } finally {
      setIsLoadingBackgrounds(false);
      console.log('🏁 Background loading complete. isLoading:', false);
    }
  };

  // Initialize default values on mount
  useEffect(() => {
    // Set default inspirational quote theme to "Motivational"
    if (!personalization?.selectedQuoteSet) {
      handleInputChange('selectedQuoteSet', 'motivational');
    }
    
    // Set "Include Cover Page" to checked by default
    if (personalization?.includeCover === undefined) {
      handleCheckboxChange('includeCover', true);
    }
    
    // Set "Page Numbers" to unchecked by default
    if (personalization?.includePageNumbers === undefined) {
      handleCheckboxChange('includePageNumbers', false);
    }
    
    // Set "Index Page" to checked by default
    if (personalization?.includeIndex === undefined) {
      handleCheckboxChange('includeIndex', true);
    }
  }, []);

  const loadFontsFromDatabase = async () => {
    try {
      setIsLoadingFonts(true);
      const fonts = await fontService?.getFonts();
      
      // Separate fonts by content_usage
      const contentFontList = [];
      const titleFontList = [];
      
      Object.entries(fonts || {})?.forEach(([fontName, variants]) => {
        // Get display_name and description from any variant (all variants have same values)
        const firstVariant = Object.values(variants)?.[0];
        const displayName = firstVariant?.displayName || 
                           fontName?.charAt(0)?.toUpperCase() + fontName?.slice(1);
        const description = firstVariant?.fontDescription || 'No description available';
        
        // For content fonts, only include fonts with content_usage=true and all 3 weights
        //if (variants?.normal && variants?.bold && variants?.italic) {
        if (variants?.normal && variants?.bold ) {
          // Check if this is a content font (content_usage=true)
          const isContentFont = variants?.normal?.contentUsage === true ||
                               variants?.bold?.contentUsage === true ||
                               variants?.italic?.contentUsage === true;
          
          if (isContentFont) {
            contentFontList?.push({
              id: fontName,
              name: displayName,
              preview: description,
              style: `font-['${displayName}']`
            });
          }
        }
        
        // For title fonts, include fonts where ANY variant has content_usage=false
        if (variants?.normal || variants?.bold) {
        //  const hasTitleUsage = Object.values(variants)?.some(
        //    (variant) => variant?.contentUsage === false
        //  );
                  const hasTitleUsage = Object.values(variants)?.some(
            (variant) => variant?.contentUsage === false || variant?.contentUsage === true
          );
          
          if (hasTitleUsage) {
            titleFontList?.push({
              id: fontName,
              name: displayName,
              preview: description,
              style: `font-['${displayName}']`
            });
          }
        }
      });
      
      
      
      setContentFonts(contentFontList);
      setTitleFonts(titleFontList);

      console.log('CONTENT FONTS: ', contentFontList);
      console.log('TITLE FONTS: ', titleFontList);
      
      // Set default font if none selected (use first content font)
      if (!personalization?.font && contentFontList?.length > 0) {
        handleInputChange('font', contentFontList?.[0]?.id);
      }
      
    } catch (error) {
      console.error('Error loading fonts from database:', error);
      // Fallback to empty arrays if loading fails
      setContentFonts([]);
      setTitleFonts([]);
    } finally {
      setIsLoadingFonts(false);
    }
  };

  const handleInputChange = (field, value) => {
    onPersonalizationChange({
      ...personalization,
      [field]: value
    });
  };

  const handleCheckboxChange = (field, checked) => {
    onPersonalizationChange({
      ...personalization,
      [field]: checked
    });
  };


  const quoteFrequencyOptions = [
    { value: 'daily', label: 'Every Day', description: 'New quote each day' },
    { value: 'weekly', label: 'Weekly', description: 'New quote each week' },
    { value: 'monthly', label: 'Monthly', description: 'New quote each month' }
  ];

  // Only Motivational enabled, others marked as "Coming Soon"
  const quoteSetOptions = [
    { value: 'motivational', label: 'Motivational', description: 'Inspiring and uplifting quotes' },
    { value: 'productivity', label: 'Productivity (Coming Soon)', description: 'Focus and achievement quotes', disabled: true },
    { value: 'mindfulness', label: 'Mindfulness (Coming Soon)', description: 'Peace and reflection quotes', disabled: true },
    { value: 'success', label: 'Success (Coming Soon)', description: 'Leadership and growth quotes', disabled: true },
    { value: 'nature', label: 'Nature (Coming Soon)', description: 'Earth and natural beauty quotes', disabled: true }
  ];

  // Generate year options for the next 10 years
  const currentYear = new Date()?.getFullYear();
  const lastYear = currentYear -1;
  const yearOptions = Array.from({ length: 3 }, (_, i) => ({
    value: String(lastYear + i),
    label: String(lastYear + i),
    description: lastYear + i === currentYear ? 'Current Year' : ''
  }));

  const sampleQuotes = {
    motivational: [
      "The only way to do great work is to love what you do. - Steve Jobs",
      "Believe you can and you're halfway there. - Theodore Roosevelt",
      "Success is not final, failure is not fatal: it is the courage to continue that counts. - Winston Churchill"
    ],
    productivity: [
      "The way to get started is to quit talking and begin doing. - Walt Disney",
      "Don\'t wait for opportunity. Create it. - Unknown",
      "Focus on being productive instead of busy. - Tim Ferriss"
    ],
    mindfulness: [
      "The present moment is the only time over which we have dominion. - Thich Nhat Hanh",
      "Peace comes from within. Do not seek it without. - Buddha",
      "Mindfulness is about being fully awake in our lives. - Jon Kabat-Zinn"
    ],
    success: [
      "Success is not the key to happiness. Happiness is the key to success. - Albert Schweitzer",
      "The difference between ordinary and extraordinary is that little extra. - Jimmy Johnson",
      "Your limitation—it\'s only your imagination. - Unknown"
    ],
    nature: [
      "In every walk with nature, one receives far more than they seek. - John Muir",
      "The earth has music for those who listen. - George Santayana",
      "Nature does not hurry, yet everything is accomplished. - Lao Tzu"
    ]
  };

  const getCurrentQuoteSample = () => {
    const quotes = sampleQuotes?.[personalization?.selectedQuoteSet] || sampleQuotes?.motivational;
    return quotes?.[0] || '';
  };

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Personal Information - ENHANCED PROMINENCE */}
      <div className="space-y-6 p-6 rounded-xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <Icon name="User" size={20} className="text-primary" />
          </div>
          <h3 className="font-heading text-2xl font-bold text-foreground">
            Personal Information
          </h3>
        </div>
        
        <div className="grid grid-cols-1 gap-6">
          {/* Your Name - ENHANCED */}
          <div className="bg-background rounded-lg p-4 shadow-sm border border-primary/20">
            <label className="block font-heading text-xl font-bold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="UserCircle" size={24} className="text-primary" />
              <span>Your Name</span>
              <span className="text-error text-lg">*</span>
            </label>
            <Input
              type="text"
              placeholder="Enter your name"
              value={personalization?.name || ''}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              className="text-xl py-4 font-semibold border-2"
            />
            <p className="font-body text-sm text-muted-foreground mt-3 flex items-center space-x-2">
              <Icon name="Info" size={16} />
              <span>This will appear prominently on your planner cover</span>
            </p>
          </div>
          
          {/* Year - ENHANCED */}
          <div className="bg-background rounded-lg p-4 shadow-sm border border-primary/20">
            <label className="block font-heading text-xl font-bold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="Calendar" size={24} className="text-primary" />
              <span>Year</span>
              <span className="text-error text-lg">*</span>
            </label>
            <Select
              value={String(personalization?.year || currentYear)}
              onChange={(value) => handleInputChange('year', parseInt(value))}
              options={yearOptions}
              placeholder="Select year"
              className="text-xl py-4 border-2"
            />
            <p className="font-body text-sm text-muted-foreground mt-3 flex items-center space-x-2">
              <Icon name="Info" size={16} />
              <span>Select the year for your planner</span>
            </p>
          </div>

          {/* Watermark Text - NEW FIELD */}
          <div className="bg-background rounded-lg p-4 shadow-sm border border-primary/20">
            <label className="block font-heading text-xl font-bold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="Type" size={24} className="text-primary" />
              <span>Watermark Text</span>
              <span className="text-sm font-normal text-muted-foreground">(Optional)</span>
            </label>
            <Input
              type="text"
              placeholder="Enter watermark text (max 10 characters)"
              value={personalization?.backgroundText || ''}
              onChange={(e) => {
                const value = e?.target?.value;
                if (value?.length <= 10) {
                  handleInputChange('backgroundText', value);
                }
              }}
              maxLength={10}
              className="text-xl py-4 font-semibold border-2"
            />
            <p className="font-body text-sm text-muted-foreground mt-3 flex items-center space-x-2">
              <Icon name="Info" size={16} />
              <span>This word will appear as background text on each page (10 characters max)</span>
            </p>
            {personalization?.backgroundText && (
              <p className="font-caption text-xs text-primary mt-2">
                {personalization?.backgroundText?.length}/10 characters
              </p>
            )}
          </div>
        </div>
        
        {/* Custom Cover Quote - ENHANCED */}
        <div className="bg-background rounded-lg p-4 shadow-sm border border-primary/20">
          <label className="block font-heading text-xl font-bold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Quote" size={24} className="text-primary" />
            <span>Custom Cover Quote</span>
            <span className="text-sm font-normal text-muted-foreground">(Optional)</span>
          </label>
          <textarea
            placeholder="Enter a personal quote or motto for your cover (e.g., 'Dream big, work hard, stay focused')"
            value={personalization?.quote || ''}
            onChange={(e) => handleInputChange('quote', e?.target?.value)}
            rows={4}
            className="w-full px-4 py-4 text-lg font-medium rounded-lg border-2 border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          />
          <p className="font-body text-sm text-muted-foreground mt-3 flex items-center space-x-2">
            <Icon name="Info" size={16} />
            <span>This quote will be featured prominently on your cover page</span>
          </p>
        </div>
      </div>

      {/* Inspirational Quotes Section - UPDATED with disabled options */}
      <div className="space-y-4">
        <h3 className="font-heading text-lg font-semibold text-foreground">
          Inspirational Quotes
        </h3>
        
        <div className="flex items-start space-x-3 p-4 rounded-lg border border-border bg-card">
          <Checkbox
            checked={personalization?.includeBottomQuotes || false}
            onChange={(e) => handleCheckboxChange('includeBottomQuotes', e?.target?.checked)}
            className="mt-1"
          />
          <div className="flex-1">
            <h4 className="font-body font-medium text-foreground mb-2">
              Include Inspirational Quotes at Bottom of Pages
            </h4>
            <p className="font-caption text-xs text-muted-foreground mb-3">
              Add motivational quotes at the bottom of your planner pages to inspire and motivate you throughout the year
            </p>
            
            {personalization?.includeBottomQuotes && (
              <div className="space-y-4 mt-4 pl-4 border-l-2 border-primary/20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-body text-sm font-medium text-foreground mb-2">
                      Quote Frequency
                    </label>
                    <Select
                      value={personalization?.quoteFrequency || 'daily'}
                      onChange={(value) => handleInputChange('quoteFrequency', value)}
                      options={quoteFrequencyOptions}
                      placeholder="Select frequency"
                    />
                  </div>
                  
                  <div>
                    <label className="block font-body text-sm font-medium text-foreground mb-2 flex items-center space-x-2">
                      <span>Quote Theme</span>
                      <span className="text-xs font-normal text-primary bg-primary/10 px-2 py-0.5 rounded">
                        Default: Motivational
                      </span>
                    </label>
                    <Select
                      value={personalization?.selectedQuoteSet || 'motivational'}
                      onChange={(value) => handleInputChange('selectedQuoteSet', value)}
                      options={quoteSetOptions}
                      placeholder="Select theme"
                    />
                    <p className="font-caption text-xs text-muted-foreground mt-2 flex items-center space-x-1">
                      <Icon name="Info" size={12} />
                      <span>Additional themes coming soon</span>
                    </p>
                  </div>
                </div>
                
                {/* Quote Preview */}
                <div className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-body text-sm font-medium text-foreground">
                      Sample Quote Preview
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowQuotePreview(!showQuotePreview)}
                      className="flex items-center space-x-1 text-primary hover:text-primary/80 text-xs"
                    >
                      <Icon name={showQuotePreview ? "ChevronUp" : "ChevronDown"} size={14} />
                      <span>{showQuotePreview ? 'Hide' : 'Show'} Preview</span>
                    </button>
                  </div>
                  
                  {showQuotePreview && (
                    <div className="mt-3 p-3 bg-background rounded border-l-4 border-primary">
                      <p className="font-caption text-sm italic text-muted-foreground text-center">
                        "{getCurrentQuoteSample()}"
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Font Selection - Title fonts filtered to content_usage=false only */}
      <div className="space-y-6">
        <h3 className="font-heading text-lg font-semibold text-foreground">
          Typography
        </h3>
        
        {/* Title Font Section - Only non-content fonts */}
        <div className="space-y-4">
          <div>
            <h4 className="font-body text-base font-semibold text-foreground mb-1">
              Title Font (Optional)
            </h4>
            <p className="font-caption text-xs text-muted-foreground mb-3">
              Choose a decorative font for cover page, headers, and major titles. Leave empty to use content font.
            </p>
          </div>
          
          {isLoadingFonts ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="text-sm text-muted-foreground mt-2">Loading fonts...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* "None" option to use content font */}
              <div
                onClick={() => handleInputChange('titleFont', null)}
                className={`
                  p-4 rounded-lg border-2 cursor-pointer transition-all duration-200
                  hover:shadow-md hover:scale-[1.02]
                  ${!personalization?.titleFont
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border bg-card hover:border-primary/50'
                  }
                `}
              >
                <div className="text-center space-y-2">
                  <div className="text-2xl font-sans">
                    —
                  </div>
                  <h5 className="font-heading font-semibold text-foreground">
                    None
                  </h5>
                  <p className="font-caption text-xs text-muted-foreground">
                    Use content font
                  </p>
                  
                  <div className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center mx-auto
                    ${!personalization?.titleFont
                      ? 'border-primary bg-primary' :'border-muted-foreground'
                    }
                  `}>
                    {!personalization?.titleFont && (
                      <Icon name="Check" size={12} color="white" />
                    )}
                  </div>
                </div>
              </div>
              
              {/* Only title fonts (content_usage=false) */}
              {titleFonts?.map((font) => (
                <div
                  key={`title-${font?.id}`}
                  onClick={() => handleInputChange('titleFont', font?.id)}
                  className={`
                    p-4 rounded-lg border-2 cursor-pointer transition-all duration-200
                    hover:shadow-md hover:scale-[1.02]
                    ${personalization?.titleFont === font?.id
                      ? 'border-primary bg-primary/5 shadow-md'
                      : 'border-border bg-card hover:border-primary/50'
                    }
                  `}
                >
                  <div className="text-center space-y-2">
                    <div 
                      className="text-2xl"
                      style={{ fontFamily: font?.name }}
                    >
                      Aa
                    </div>
                    <h5 className="font-heading font-semibold text-foreground">
                      {font?.name}
                    </h5>
                    <p className="font-caption text-xs text-muted-foreground">
                      {font?.preview}
                    </p>
                    
                    <div className={`
                      w-5 h-5 rounded-full border-2 flex items-center justify-center mx-auto
                      ${personalization?.titleFont === font?.id
                        ? 'border-primary bg-primary' :'border-muted-foreground'
                      }
                    `}>
                      {personalization?.titleFont === font?.id && (
                        <Icon name="Check" size={12} color="white" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Content Font Section - Unchanged */}
        <div className="space-y-4">
          <div>
            <h4 className="font-body text-base font-semibold text-foreground mb-1">
              Content Font
            </h4>
            <p className="font-caption text-xs text-muted-foreground mb-3">
              Choose a font for all body text throughout your planner
            </p>
          </div>
          
          {isLoadingFonts ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="text-sm text-muted-foreground mt-2">Loading fonts...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {contentFonts?.map((font) => (
                <div
                  key={`content-${font?.id}`}
                  onClick={() => handleInputChange('font', font?.id)}
                  className={`
                    p-4 rounded-lg border-2 cursor-pointer transition-all duration-200
                    hover:shadow-md hover:scale-[1.02]
                    ${personalization?.font === font?.id
                      ? 'border-primary bg-primary/5 shadow-md'
                      : 'border-border bg-card hover:border-primary/50'
                    }
                  `}
                >
                  <div className="text-center space-y-2">
                    <div 
                      className="text-2xl"
                      style={{ fontFamily: font?.name }}
                    >
                      Aa
                    </div>
                    <h5 className="font-heading font-semibold text-foreground">
                      {font?.name}
                    </h5>
                    <p className="font-caption text-xs text-muted-foreground">
                      {font?.preview}
                    </p>
                    
                    <div className={`
                      w-5 h-5 rounded-full border-2 flex items-center justify-center mx-auto
                      ${personalization?.font === font?.id
                        ? 'border-primary bg-primary' :'border-muted-foreground'
                      }
                    `}>
                      {personalization?.font === font?.id && (
                        <Icon name="Check" size={12} color="white" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Cover Options - UPDATED: Add background image selector */}
      <div className="space-y-4">
        <h3 className="font-heading text-lg font-semibold text-foreground">
          Cover Options
        </h3>
        
        <div className="space-y-3">
          {/* Include Cover Page - Always checked, disabled */}
          <div className="flex items-start space-x-3 p-3 rounded-lg border-2 border-primary bg-primary/5">
            <Checkbox
              checked
              disabled
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h4 className="font-body font-medium text-foreground">
                  Include Cover Page
                </h4>
                <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                  Required
                </span>
              </div>
              <p className="font-caption text-xs text-muted-foreground">
                Your planner will include a beautiful cover page with your name and year
              </p>
            </div>
          </div>

          {/* NEW: Cover Background Image Selection */}
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="mb-4">
              <h4 className="font-body text-base font-semibold text-foreground mb-1">
                Cover Background Image
              </h4>
              <p className="font-caption text-xs text-muted-foreground">
                Choose a background image for your cover page or select "No Image" for a solid color cover
              </p>
            </div>

            {isLoadingBackgrounds ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="text-sm text-muted-foreground mt-2">Loading background images...</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* "No Image" Option */}
                <div
                  onClick={() => handleInputChange('coverBackgroundImage', null)}
                  className={`
                    relative aspect-[3/4] rounded-lg border-2 cursor-pointer transition-all duration-200
                    hover:shadow-md hover:scale-[1.02]
                    ${!personalization?.coverBackgroundImage
                      ? 'border-primary bg-primary/5 shadow-md'
                      : 'border-border bg-muted hover:border-primary/50'
                    }
                  `}
                >
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-3">
                    <Icon name="X" size={32} className={personalization?.coverBackgroundImage ? 'text-muted-foreground' : 'text-primary'} />
                    <p className="text-sm font-medium text-center mt-2">
                      No Image
                    </p>
                  </div>
                  
                  {/* Selection Indicator */}
                  <div className={`
                    absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center
                    ${!personalization?.coverBackgroundImage
                      ? 'border-primary bg-primary' :'border-muted-foreground bg-background'
                    }
                  `}>
                    {!personalization?.coverBackgroundImage && (
                      <Icon name="Check" size={14} color="white" />
                    )}
                  </div>
                </div>

                {/* Background Image Options */}
                {backgroundImages?.map((bg) => (
                  <div
                    key={bg?.name}
                    onClick={() => handleInputChange('coverBackgroundImage', bg?.name)}
                    className={`
                      relative aspect-[3/4] rounded-lg border-2 cursor-pointer transition-all duration-200
                      overflow-hidden hover:shadow-md hover:scale-[1.02]
                      ${personalization?.coverBackgroundImage === bg?.name
                        ? 'border-primary shadow-md'
                        : 'border-border hover:border-primary/50'
                      }
                    `}
                  >
                    {/* Background Image Preview */}
                    <img
                      src={bg?.url}
                      alt={`Background ${bg?.name}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    
                    {/* Overlay for better contrast */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    
                    {/* Image Name Label */}
                    <div className="absolute bottom-2 left-2 right-2">
                      <p className="text-xs font-medium text-white text-center truncate">
                        {bg?.name?.replace('pdf-background', 'Background ')?.replace('.jpeg', '')}
                      </p>
                    </div>
                    
                    {/* Selection Indicator */}
                    <div className={`
                      absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center
                      ${personalization?.coverBackgroundImage === bg?.name
                        ? 'border-white bg-primary' :'border-white/50 bg-white/20'
                      }
                    `}>
                      {personalization?.coverBackgroundImage === bg?.name && (
                        <Icon name="Check" size={14} color="white" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Info Message */}
            <div className="mt-4 flex items-start space-x-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
              <Icon name="Info" size={16} className="mt-0.5" />
              <p className="text-xs">
                The selected background will be applied to your planner's cover page. Choose "No Image" for a clean, solid color cover.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Page Settings - UPDATED: Page numbers unchecked+disabled, Index checked+disabled */}
      <div className="space-y-4">
        <h3 className="font-heading text-lg font-semibold text-foreground">
          Page Settings
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Page Numbers - Unchecked and disabled */}
          <div className="flex items-start space-x-3 p-3 rounded-lg border border-border bg-card opacity-60">
            <Checkbox
             
              disabled
              className="mt-1"
            />
            <div>
              <div className="flex items-center space-x-2">
                <h4 className="font-body font-medium text-muted-foreground">
                  Page Numbers
                </h4>
                <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  Coming Soon
                </span>
              </div>
              <p className="font-caption text-xs text-muted-foreground">
                Page numbering feature will be available in future updates
              </p>
            </div>
          </div>
          
          {/* Index Page - Checked and disabled */}
          <div className="flex items-start space-x-3 p-3 rounded-lg border-2 border-primary bg-primary/5">
            <Checkbox
              checked
              disabled
              className="mt-1"
            />
            <div>
              <div className="flex items-center space-x-2">
                <h4 className="font-body font-medium text-foreground">
                  Index Page
                </h4>
                <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                  Required
                </span>
              </div>
              <p className="font-caption text-xs text-muted-foreground">
                Your planner will include an index for easy navigation
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalizationOptions;